import { Component, OnInit, ViewEncapsulation, Input } from '@angular/core';
import {
    LoginActivity,
    LoginActivityService,
} from '../../../shared/services/login-activity.service';
import { GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import {
    SortDescriptor,
    GroupDescriptor,
    orderBy,
    filterBy,
    process,
} from '@progress/kendo-data-query';
import { IntlService } from '@progress/kendo-angular-intl';
import { addMonths } from '@progress/kendo-date-math';
import { SelectionRange } from '@progress/kendo-angular-dateinputs';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-lba-login-activity',
    templateUrl: './lba-login-activity.component.html',
    styleUrls: ['./lba-login-activity.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class LbaLoginActivityComponent implements OnInit {
    @Input() view: string;

    data: LoginActivity[];
    exportData: LoginActivity[];
    gridView: GridDataResult;
    filteredData: LoginActivity[];
    groups: GroupDescriptor[] = [{ field: 'fullName' }];
    exportFileName: string;
    sort: SortDescriptor[];
    sortable = {
        mode: 'single',
        allowUnsort: true,
    };
    loginActivitySearchTerm: string;
    type: 'numeric' | 'input' = 'input';
    skip = 0;
    pageSize = 10;
    groupable = true;
    scrollable = true;
    pageable = {
        refresh: true,
        info: true,
        type: this.type,
        previousNext: true,
        pageSizes: [5, 10, 20, 100],
    };
    range: SelectionRange = {
        start: addMonths(new Date(), -1),
        end: new Date(),
    };
    animation = {
        type: 'slide',
        duration: 200,
    };
    timeZoneGMTVariance: string;

    constructor(
        private service: LoginActivityService,
        private intl: IntlService,
        private currentUserStateService: CurrentUserStateService,
        private _reusableFunctions: ReusableFunctions,
        private datePipe: DatePipe
    ) {
        this.getExcelExportData = this.getExcelExportData.bind(this);
    }

    ngOnInit() {
        this.setTimeZoneByUser();
        this.getData();
        if (this.view === 'admin') {
            this.sort = [
                {
                    field: 'clientShortName',
                    dir: 'asc',
                },
            ];
        } else {
            this.sort = [
                {
                    field: 'fullName',
                    dir: 'asc',
                },
            ];
        }
        this.exportData = [];
    }

    setTimeZoneByUser(): void {
        this.timeZoneGMTVariance =
            this.currentUserStateService.currentUser.timeZoneGMTVariance.toString();
    }

    getData() {
        //pass utc dates
        this.service
            .getLoginActivity(
                this.formatDate(this.range.start),
                this.formatDate(this.range.end),
                this.timeZoneGMTVariance,
                this.view === 'client' // if view = client then we want to filterByClient, otherwise we return all activity (LBA User)
            )
            .subscribe((res) => {
                this.data = res;
                this.updateGrid();
            });
    }

    updateGrid() {
        let localData = this.filteredData ? this.filteredData : this.data;
        let orderedData = orderBy(localData, this.sort);
        let groupedData = process(orderedData, {
            group: this.groups,
        });

        this.exportData = this.data;
        this.exportData.forEach(
            (d) => (d.friendlyTime = this.datePipe.transform(d.lastLoginDate, 'short'))
        );

        //total elements(bottom right of grid) is now the # of grouped arrays inside collection
        //this increases number of records shown
        let length = groupedData.data.length;
        groupedData.data = groupedData.data.slice(this.skip, this.skip + this.pageSize);
        this.gridView = {
            data: groupedData.data,
            total: length,
        };
    }

    filterLoginActivity(): void {
        let searchTermTokens = this.loginActivitySearchTerm.split(' ');
        this.filteredData = filterBy(this.data, {
            logic: 'or',
            filters: [
                {
                    field: 'clientShortName',
                    operator: 'contains',
                    value: searchTermTokens[0],
                    ignoreCase: true,
                },
                {
                    field: 'lastName',
                    operator: 'contains',
                    value: searchTermTokens[0],
                    ignoreCase: true,
                },
                {
                    field: 'firstName',
                    operator: 'contains',
                    value: searchTermTokens[0],
                    ignoreCase: true,
                },
                {
                    field: 'jobTitle',
                    operator: 'contains',
                    value: searchTermTokens[0],
                    ignoreCase: true,
                },
                {
                    field: 'loginCount',
                    operator: 'contains',
                    value: searchTermTokens[0],
                    ignoreCase: true,
                },
            ],
        });
        if (searchTermTokens.length > 1) {
            this.filteredData = filterBy(this.data, {
                logic: 'or',
                filters: [
                    {
                        field: 'clientShortName',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'lastName',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'firstName',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'jobTitle',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                ],
            });
        }
        this.skip = 0;
        this.updateGrid();
    }

    groupChange(groups: GroupDescriptor[]): void {
        this.groups = groups;
        this.updateGrid();
    }

    pageChange(event: PageChangeEvent): void {
        this.pageSize = event.take;
        this.skip = event.skip;
        this.updateGrid();
    }

    sortChange(sort: SortDescriptor[]): void {
        this.sort = sort;
        this.skip = 0;
        this.updateGrid();
    }

    onChange(value?: Date) {
        this.getData();
    }

    formatDate(value?: Date): string {
        return value ? this.intl.formatDate(value, 'MM/dd/yyyy') : '';
    }

    exportToExcel(kendoGrid: GridComponent) {
        let dateTime = this._reusableFunctions.getCurrentDateTimeStamp();
        this.exportFileName = 'NV_LoginActivity_' + dateTime + '_.xlsx';
        kendoGrid.saveAsExcel();
    }

    getExcelExportData(): ExcelExportData {
        const result: ExcelExportData = {
            data: this.exportData,
            group: [],
        };
        return result;
    }
}
